/**
 * FusionBankImport — trigger a REAL bank-statement upload to bank-analyser from
 * the fusion workbench, then drop the parsed statement onto the canvas as the
 * bank surface (account + top counterparties), and deep-link into bank-analyser
 * for the full ledger. Config-gated: dark until VITE_STATEMENTFLOW_URL is set.
 *
 * After parse: auto-fills account holder from KYC, stores a Case Files dossier
 * (txs / CPs / Dr·Cr / holder / account #), and on a real case writes Evidence.
 */
import { useEffect, useState } from 'react';
import type cytoscape from 'cytoscape';
import { Upload, Loader2, ExternalLink, CheckCircle2, AlertTriangle, Banknote, Info, Lock, Eye, EyeOff, FolderOpen } from 'lucide-react';
import {
  statementFlow,
  type SFStatement,
  type SFAccountKyc,
  isPdfPasswordRequiredError,
  pdfLooksPasswordProtected,
} from '../../services/statementFlow';
import { statementToElements } from '../../data/fusionBankAdapter';
import { linkDerivedIdentifiers, linkToSubject, setSubjectName, normalizeIdentifier } from '../../case-canvas/entityJoin';
import { persistCaseEdge } from '../../services/caseCanvas';
import { useFusionStore } from '../../stores/useFusionStore';
import { useIntakeStore } from '../../stores/useIntakeStore';
import { useBankImportStore } from '../../stores/useBankImportStore';
import { saveBankDossierAsEvidence, persistBankDossierEdges } from '../../data/promoteBankImport';
import { FileDropzone } from '../ui/FileDropzone';
import { runLayout, fitTo, visibleEles, bloom } from './fusionLayout';
import { isGuestDemo } from '../../utils/guestDemo';

const HUB_ID = 'id:rkumar';   // link imported account into the demo case identity hub, if present

type Phase = 'idle' | 'creating' | 'uploading' | 'parsing' | 'fetching' | 'done' | 'error';

function persistBankSubjectEdge(accountNodeId: string, accountLabel: string, accountNumber?: string | null) {
  const caseId = useIntakeStore.getState().caseId;
  if (!caseId || !accountNodeId) return;
  const raw = accountNumber?.trim()
    || accountNodeId.replace(/^ent:account:/, '').replace(/^sf:acct:/, '').replace(/^sf:account:/, '');
  const canon = normalizeIdentifier('account', raw) || raw;
  persistCaseEdge(caseId, {
    source: { module: 'shared', type: 'identity_hub', canonical_id: 'case', label: 'Subject (your data)' },
    target: { module: 'bank', type: 'account', canonical_id: canon, label: accountLabel || canon },
    edge_type: 'cross_link',
    determinism: 'scored',
    attribution: { source: 'fusion', method: 'bank_import_subject' },
  });
}

export function FusionBankImport({
  onOpenCaseFiles,
}: {
  /** Switch aside to Case files after a successful import. */
  onOpenCaseFiles?: () => void;
} = {}) {
  const configured = statementFlow.isConfigured();
  const select = useFusionStore((s) => s.select);
  const setShowDemo = useFusionStore((s) => s.setShowDemo);
  const setDossier = useBankImportStore((s) => s.setDossier);
  const [file, setFile] = useState<File | null>(null);
  const [person, setPerson] = useState('');
  const [holderSource, setHolderSource] = useState<'typed' | 'detected' | null>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [msg, setMsg] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [statement, setStatement] = useState<SFStatement | null>(null);
  const [accountKyc, setAccountKyc] = useState<SFAccountKyc | null>(null);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ txns: number; cps: number; dr: number; cr: number } | null>(null);

  const adoptFile = async (f: File | null) => {
    setFile(f);
    setErr(null);
    setPassword('');
    setShowPassword(false);
    setNeedsPassword(false);
    setPhase('idle');
    setMsg('');
    setStatement(null);
    setSummary(null);
    setAccountKyc(null);
    setHolderSource(null);
    if (!f) return;
    const locked = await pdfLooksPasswordProtected(f);
    if (locked) setNeedsPassword(true);
  };

  // A file dropped onto the canvas is routed here.
  useEffect(() => {
    const onDropFile = (e: Event) => {
      const f = (e as CustomEvent).detail as File | undefined;
      if (f) void adoptFile(f);
    };
    window.addEventListener('fusion:drop-file', onDropFile);
    return () => window.removeEventListener('fusion:drop-file', onDropFile);
  }, []);

  const busy = phase !== 'idle' && phase !== 'done' && phase !== 'error';

  const run = async () => {
    if (!file) { setErr('Choose a statement file first (PDF / XLSX / CSV).'); return; }
    if (needsPassword && !password.trim()) {
      setErr('This PDF is password-protected. Enter the statement password to continue.');
      setPhase('error');
      return;
    }
    setErr(null); setStatement(null); setSummary(null); setAccountKyc(null); setHolderSource(null);
    try {
      setPhase('creating'); setMsg('Preparing case in StatementFlow…');
      const cid = await statementFlow.ensureCase();
      setCaseId(cid);
      // Person name: typed override, else temporary Subject until KYC returns.
      const pid = await statementFlow.ensurePerson(cid, person.trim() || 'Subject');

      setPhase('uploading'); setMsg(`Uploading & parsing ${file.name}…`);
      const pw = password.trim() || undefined;
      const up = await statementFlow.uploadStatement(cid, pid, file, pw ? { password: pw } : undefined);
      setStatement(up.statement);

      // Upload is synchronous — skip poll when rows already returned.
      setPhase('parsing'); setMsg('Confirming parse + detecting account holder…');
      const st = await statementFlow.waitForParse(up.statement.id, {
        seed: up.statement,
        transactionCount: up.transaction_count,
      });
      setStatement(st);

      let kyc: SFAccountKyc | null = up.account || null;
      if (!kyc?.ifsc && !kyc?.holder_name) {
        kyc = await statementFlow.getAccount(cid, st.account_id);
      }
      setAccountKyc(kyc);

      const extractedHolder = (kyc?.holder_name || '').trim();
      if (extractedHolder) {
        setPerson(extractedHolder);
        setHolderSource('detected');
      } else if (person.trim()) {
        setHolderSource('typed');
      }

      setPhase('fetching'); setMsg('Loading transactions…');
      const txns = await statementFlow.getTransactions(cid, st.account_id);

      const cy = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
      const hasHub = !!cy && cy.getElementById(HUB_ID).length > 0;
      const accountLabel = extractedHolder
        || st.source_file_name?.replace(/\.[^.]+$/, '')
        || 'Imported account';
      const res = statementToElements({
        accountId: st.account_id,
        accountLabel,
        txns,
        linkHubId: hasHub ? HUB_ID : undefined,
        kyc,
      });

      if (cy) {
        cy.elements().filter((el) => el.id().startsWith('sf:')).remove();  // replace any prior import
        // Canonical `ent:` counterparties may already exist (pasted card, image
        // artefact) — merge onto them instead of throwing on a duplicate id. The
        // Dr/Cr edges still land on the existing node: that IS the cross-link.
        const fresh = res.elements.filter((e) => {
          const eid = (e.data as { id?: string }).id;
          return !eid || cy.getElementById(eid).empty();
        });
        cy.add(fresh);
        // The account is the subject's own → hang it off the subject hub. Its
        // counterparties are third parties and stay linked only to the account.
        linkToSubject(cy, res.accountNodeId);
        persistBankSubjectEdge(
          res.accountNodeId,
          String((cy.getElementById(res.accountNodeId).data('label') as string) || res.accountNodeId),
          kyc?.account_number,
        );
        // Always prefer OCR/extracted holder for the case subject when present.
        const typedHolder = (person || '').trim();
        const holder = extractedHolder || typedHolder;
        if (holder) {
          useIntakeStore.getState().setSubjectName(holder);
          setSubjectName(cy, holder);
        }
        linkDerivedIdentifiers(cy);
        runLayout(cy, visibleEles(cy));
        fitTo(cy, visibleEles(cy), 55);
        bloom(cy, fresh.filter((e) => !(e.data as { source?: string }).source).map((e) => (e.data as { id: string }).id));
        select({ id: res.accountNodeId, kind: 'node' });
      }

      const summ = { txns: txns.length, cps: res.counterpartyCount, dr: res.drTotal, cr: res.crTotal };
      setSummary(summ);

      const deepLink = statementFlow.deepLink(cid, st.id);
      const leCaseId = useIntakeStore.getState().caseId;
      const dossier = {
        leCaseId,
        sfCaseId: cid,
        statementId: st.id,
        accountId: st.account_id,
        accountNodeId: res.accountNodeId,
        fileName: file.name,
        statement: {
          source_file_name: st.source_file_name,
          narrative_summary: st.narrative_summary,
          risk_level: st.risk_level,
          anomalies: st.anomalies,
          extracted_txn_count: st.extracted_txn_count,
        },
        kyc,
        summary: summ,
        transactions: res.transactions,
        counterparties: res.counterparties,
        deepLink,
        importedAt: new Date().toISOString(),
      };
      setDossier(dossier);

      // Real case canvas: stash in Evidence Locker + persist account/CP edges.
      if (leCaseId && !isGuestDemo()) {
        persistBankDossierEdges(leCaseId, dossier);
        try {
          await saveBankDossierAsEvidence(leCaseId, dossier);
        } catch {
          /* non-fatal — canvas + Case files still have the dossier */
        }
      }

      // Leave demo seed so the imported statement is the focus (sandbox only).
      if (!leCaseId) setShowDemo(false);

      setPhase('done'); setMsg('');
      onOpenCaseFiles?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const passwordFail =
        (e as { passwordRequired?: boolean })?.passwordRequired === true
        || isPdfPasswordRequiredError(message);
      if (passwordFail) {
        setNeedsPassword(true);
        setErr(
          password.trim()
            ? 'Incorrect PDF password. Check the password (often DOB / account / phone digits from the bank) and try again.'
            : 'This PDF is password-protected. Enter the statement password below, then import again.',
        );
      } else {
        setErr(message);
      }
      setPhase('error'); setMsg('');
    }
  };

  const deepLink = caseId ? statementFlow.deepLink(caseId, statement?.id) : undefined;

  if (!configured) {
    return (
      <div className="p-4 text-xs space-y-2">
        <div className="flex items-center gap-2 font-semibold text-sm"><Banknote size={15} /> Bank statement import</div>
        <div className="flex items-start gap-2 rounded border border-border bg-secondary/40 p-2 text-muted-foreground">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>
            Live import is <span className="font-medium">off</span>. Set <code className="font-mono">VITE_STATEMENTFLOW_URL</code>
            {' '}(and <code className="font-mono">VITE_STATEMENTFLOW_KEY</code>) at build time, and allowlist this origin in
            StatementFlow's <code className="font-mono">ALLOWED_ORIGINS</code>.
          </div>
        </div>
        {file && (
          <div className="flex items-start gap-1.5 rounded border border-primary/30 bg-primary/5 p-2 text-primary">
            <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
            <span>Received <span className="font-medium break-all">{file.name}</span> — staged. Live import is off, so it isn't parsed yet.</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 text-xs space-y-3">
      <div className="flex items-center gap-2 font-semibold text-sm"><Banknote size={15} /> Bank statement import</div>

      <div className="space-y-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Statement file</label>
          <FileDropzone
            bare
            compact
            accept=".pdf,.xlsx,.xls,.csv,application/pdf"
            file={file}
            onFileChange={(f) => { void adoptFile(f); }}
            disabled={busy}
            description="PDF · XLSX · CSV"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Account holder
            {holderSource === 'detected' && (
              <span className="ml-1.5 normal-case tracking-normal text-emerald-600 font-medium">· auto-detected</span>
            )}
          </label>
          <input
            value={person}
            onChange={(e) => { setPerson(e.target.value); setHolderSource(e.target.value.trim() ? 'typed' : null); }}
            className="w-full text-[11px] px-2 py-1.5 bg-background border border-input rounded"
            placeholder="Auto-detected from statement after import"
            disabled={busy}
            data-testid="bank-import-holder"
          />
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            Leave blank to use the name StatementFlow reads from the PDF/KYC block. You can override anytime.
          </p>
        </div>
        {needsPassword && (
          <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2.5 space-y-1.5">
            <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium text-[11px]">
              <Lock size={12} className="shrink-0" />
              PDF password required
            </div>
            <p className="text-[10px] text-muted-foreground">
              Banks often set this to DOB (DDMMYYYY), account number digits, or mobile number. Used only to open the file for parsing — not stored.
            </p>
            <label className="block text-[10px] uppercase tracking-wide text-muted-foreground">Statement password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErr(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !busy && file) void run(); }}
                autoComplete="off"
                disabled={busy}
                className="w-full text-[11px] px-2 py-1.5 pr-8 bg-background border border-input rounded font-mono"
                placeholder="Enter PDF password"
                data-testid="bank-import-pdf-password"
              />
              <button
                type="button"
                tabIndex={-1}
                disabled={busy}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          </div>
        )}
        {!needsPassword && file && /\.pdf$/i.test(file.name) && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setNeedsPassword(true)}
            className="text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline flex items-center gap-1"
          >
            <Lock size={10} /> PDF is password-protected?
          </button>
        )}
        <button onClick={() => void run()} disabled={busy || !file || (needsPassword && !password.trim())}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded bg-primary text-primary-foreground font-medium hover:bg-brand-gold-hover disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? 'Importing…' : needsPassword ? 'Unlock & import' : 'Import & add to canvas'}
        </button>
        {busy && <div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> {msg}</div>}
        {err && <div className="text-[11px] text-red-500 flex items-start gap-1.5"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {err}</div>}
      </div>

      {phase === 'done' && summary && (
        <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5 text-emerald-600 font-semibold"><CheckCircle2 size={14} /> Statement on canvas</div>
          <div className="grid grid-cols-2 gap-1 text-[11px]">
            <span className="text-muted-foreground">Transactions</span><span className="text-right font-medium">{summary.txns}</span>
            <span className="text-muted-foreground">Counterparties</span><span className="text-right font-medium">{summary.cps}</span>
            <span className="text-muted-foreground">Debits</span><span className="text-right font-medium">₹{summary.dr.toLocaleString('en-IN')}</span>
            <span className="text-muted-foreground">Credits</span><span className="text-right font-medium">₹{summary.cr.toLocaleString('en-IN')}</span>
          </div>
          {accountKyc && (
            <div className="grid grid-cols-2 gap-1 text-[11px] border-t border-border pt-1.5">
              {accountKyc.holder_name && <><span className="text-muted-foreground">Holder</span><span className="text-right font-medium break-all">{accountKyc.holder_name}</span></>}
              {accountKyc.account_number && <><span className="text-muted-foreground">Account</span><span className="text-right font-mono text-[10px]">{accountKyc.account_number}</span></>}
              {accountKyc.ifsc && <><span className="text-muted-foreground">IFSC</span><span className="text-right font-mono text-[10px]">{accountKyc.ifsc}</span></>}
              {accountKyc.branch && <><span className="text-muted-foreground">Branch</span><span className="text-right font-medium break-all">{accountKyc.branch}</span></>}
              {accountKyc.bank_location && <><span className="text-muted-foreground">Location</span><span className="text-right font-medium break-all">{accountKyc.bank_location}</span></>}
              {accountKyc.bank_phone && <><span className="text-muted-foreground">Bank phone</span><span className="text-right font-medium">{accountKyc.bank_phone}</span></>}
              {accountKyc.bank_email && <><span className="text-muted-foreground">Bank email</span><span className="text-right font-medium break-all">{accountKyc.bank_email}</span></>}
            </div>
          )}
          {statement?.narrative_summary && <p className="text-[10px] text-muted-foreground border-t border-border pt-1">{statement.narrative_summary}</p>}
          {Array.isArray(statement?.anomalies) && statement!.anomalies!.length > 0 && (
            <div className="text-[10px] text-amber-600">{statement!.anomalies!.length} anomaly flag(s) — see StatementFlow.</div>
          )}
          {onOpenCaseFiles && (
            <button
              type="button"
              onClick={onOpenCaseFiles}
              className="w-full mt-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border border-emerald-600/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 font-medium hover:bg-emerald-500/20"
            >
              <FolderOpen size={14} /> View in Case files
            </button>
          )}
        </div>
      )}

      {deepLink && (
        <a href={deepLink} target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border border-border bg-secondary hover:bg-secondary/70 text-foreground font-medium">
          <ExternalLink size={14} /> Open full ledger in StatementFlow
        </a>
      )}
    </div>
  );
}
